import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  signal,
  untracked,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommandPaletteService } from './command-palette.service';
import { PALETTE_GROUPS, PaletteGroup, ResolvedCommand, resolveCommand, searchCommands } from './command-palette.model';
import { sentenceCaseIfShouting } from '../header/header-helpers';
import { Router } from '@angular/router';
import { MenuService } from '../sidebar/menu.service';
import { collectBranchKeys } from '../sidebar/menu-tree';
import { LayoutService } from '../../service/layout.service';
import { pageCommands, sidebarCommands } from './layout-commands';
import { isInRichTextEditor, isShortcut, shortcutLabel } from './keyboard-shortcuts';

interface PaletteRow {
  index: number;
  item: ResolvedCommand;
}

interface PaletteSection {
  group: PaletteGroup;
  rows: PaletteRow[];
}

/** Ctrl/⌘ K command palette: jump to any page, switch language or theme, and run the app's other actions. */
@Component({
  selector: 'app-command-palette',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './command-palette.component.html',
  styleUrl: './command-palette.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommandPaletteComponent implements OnDestroy {
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  readonly query = signal('');
  readonly activeIndex = signal(0);

  /** Commands translated when the palette opens (texts follow the current language). */
  private readonly items = signal<ResolvedCommand[]>([]);
  private readonly recentIds = signal<string[]>([]);

  readonly sections = computed<PaletteSection[]>(() => {
    const items = this.items();
    const query = this.query().trim();
    let index = 0;
    const section = (group: PaletteGroup, groupItems: ResolvedCommand[]): PaletteSection => ({
      group,
      rows: groupItems.map((item) => ({ index: index++, item })),
    });

    if (query) {
      // Ranked results, grouped; groups ordered by their best match.
      const results = searchCommands(items, query);
      const order = [...new Set(results.map((item) => item.command.group))];
      return order.map((group) => section(group, results.filter((item) => item.command.group === group)));
    }

    const byId = new Map(items.map((item) => [item.command.id, item]));
    const recent = this.recentIds().map((id) => byId.get(id)).filter((item): item is ResolvedCommand => !!item);
    return PALETTE_GROUPS
      .map((group) => section(group, group === 'recent' ? recent : items.filter((item) => item.command.group === group)))
      .filter((s) => s.rows.length > 0);
  });

  readonly rows = computed(() => this.sections().flatMap((s) => s.rows));

  readonly activeOptionId = computed(() => (this.rows().length ? this.optionId(this.activeIndex()) : null));

  private returnFocusTo: HTMLElement | null = null;
  private previousBodyOverflow = '';
  private readonly unregisterLayoutCommands: () => void;

  constructor(
    readonly palette: CommandPaletteService,
    private readonly translate: TranslateService,
    private readonly menuService: MenuService,
    private readonly layoutService: LayoutService,
    private readonly router: Router,
  ) {
    this.unregisterLayoutCommands = this.palette.register(() => this.layoutCommands());

    effect(() => {
      const open = this.palette.isOpen();
      untracked(() => (open ? this.onOpen() : this.onClose()));
    });
  }

  ngOnDestroy(): void {
    this.unregisterLayoutCommands();
    if (this.palette.isOpen()) this.palette.close();
    this.unlockScroll();
  }

  /**
   * Global shortcuts, from anywhere (text fields included): Ctrl/⌘ K toggles the palette,
   * Ctrl/⌘ B toggles the sidebar and Ctrl/⌘ I expands/collapses all its sections
   * (both left to rich-text editors, where they mean bold and italic).
   */
  @HostListener('document:keydown', ['$event'])
  onShortcut(event: KeyboardEvent): void {
    if (isShortcut(event, 'k')) {
      event.preventDefault();
      this.palette.toggle();
    } else if (isShortcut(event, 'b') && !isInRichTextEditor(event.target) && !this.layoutService.state.staticMenuHidden) {
      event.preventDefault();
      this.layoutService.onMenuToggle();
    } else if (isShortcut(event, 'i') && !isInRichTextEditor(event.target) && this.menuService.tree().length && !this.layoutService.state.staticMenuHidden) {
      event.preventDefault();
      this.menuService.toggleAll(collectBranchKeys([...this.menuService.tree()]));
    }
  }

  onQuery(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
    this.activeIndex.set(0);
  }

  onKeydown(event: KeyboardEvent): void {
    const count = this.rows().length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (count) this.setActive((this.activeIndex() + 1) % count);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (count) this.setActive((this.activeIndex() - 1 + count) % count);
        break;
      case 'Home':
        if (count && !this.query()) {
          event.preventDefault();
          this.setActive(0);
        }
        break;
      case 'End':
        if (count && !this.query()) {
          event.preventDefault();
          this.setActive(count - 1);
        }
        break;
      case 'Enter': {
        event.preventDefault();
        const row = this.rows()[this.activeIndex()];
        if (row) this.run(row.item);
        break;
      }
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.palette.close();
        break;
      case 'Tab':
        // Focus stays in the search field while the palette is open.
        event.preventDefault();
        break;
    }
  }

  /** Pointer movement (not mere hover) selects, so scrolling with the keyboard under a still mouse keeps the selection. */
  onPointerMove(index: number): void {
    if (this.activeIndex() !== index) this.activeIndex.set(index);
  }

  run(item: ResolvedCommand): void {
    this.palette.remember(item.command.id);
    this.palette.close();
    item.command.run();
  }

  close(): void {
    this.palette.close();
  }

  optionId(index: number): string {
    return `command-palette-option-${index}`;
  }

  trackSection(_: number, section: PaletteSection): string {
    return section.group;
  }

  trackRow(_: number, row: PaletteRow): string {
    return `${row.index}:${row.item.command.id}`;
  }

  /** Every page of the menu, and the sidebar actions. */
  private layoutCommands() {
    const tree = this.menuService.tree();
    const path = this.menuService.activePath();
    const state = this.layoutService.state;
    return [
      ...pageCommands(tree, path[path.length - 1]?.key, (routerLink) => void this.router.navigateByUrl(this.router.createUrlTree(routerLink))),
      ...sidebarCommands({
        canCollapse: this.layoutService.isDesktop() && !state.staticMenuHidden,
        collapsed: state.staticMenuDesktopInactive,
        hasMenu: tree.length > 0 && !state.staticMenuHidden,
        toggleCollapsed: () => this.layoutService.onMenuToggle(),
        toggleShortcut: shortcutLabel('B'),
        expandCollapseShortcut: shortcutLabel('I'),
        expandAll: () => this.menuService.setExpanded(collectBranchKeys([...tree]), true),
        collapseAll: () => this.menuService.setExpanded(collectBranchKeys([...tree]), false),
      }),
    ];
  }

  private setActive(index: number): void {
    this.activeIndex.set(index);
    document.getElementById(this.optionId(index))?.scrollIntoView({ block: 'nearest' });
  }

  private onOpen(): void {
    const active = document.activeElement;
    this.returnFocusTo = active instanceof HTMLElement && active !== document.body ? active : null;
    const translate = (key: string) => sentenceCaseIfShouting(this.translate.instant(key));
    this.items.set(this.palette.commands().map((command) => resolveCommand(command, translate)));
    this.recentIds.set(this.palette.recentIds());
    this.query.set('');
    this.activeIndex.set(0);
    this.lockScroll();
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  private onClose(): void {
    this.unlockScroll();
    const target = this.returnFocusTo;
    this.returnFocusTo = null;
    if (target?.isConnected) target.focus();
  }

  private lockScroll(): void {
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  private unlockScroll(): void {
    if (document.body.style.overflow === 'hidden') document.body.style.overflow = this.previousBodyOverflow;
  }
}
